import React, { useCallback } from 'react';

import Drawer from '@mui/material/Drawer';
import { styled } from '@mui/material/styles';
import { IconButton, Stack, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import TextFieldWithDebounce from 'ui/WithDebounce/TextFieldWithDebounce';
import { startAdornment } from 'ui/AgGridReact/CurrencyInputAdornment';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { parseNumericInput } from 'helpers/UIHelpers';
import NumberFormatCustom from 'components/NumberFormat/NumberFormatCustom';
import { getNewFormattedValue } from '../helpers/dataHelpers';
import { UpdateProjectionDetailsParams } from '../helpers/colDefinitions';
import { useUpdateAtterrissageProjectionDetails } from '../hooks/atterrissage';
import { ProjectionVentilationData } from '../helpers/types';

const StyHeader = styled('div', {
    name: 'StyHeader',
})({
    display: 'flex',
    justifyContent: 'space-between',
});

const StyText = styled('div', {
    name: 'StyText',
})({
    display: 'flex',
    alignItems: 'center',
});

type HeaderProps = {
    onClose: React.MouseEventHandler<HTMLButtonElement>;
    title: string;
};

const Header = (props: HeaderProps): React.ReactElement => {
    const { onClose, title } = props;

    return (
        <StyHeader>
            <StyText>
                <Typography variant="h4">{title}</Typography>
            </StyText>
            <IconButton aria-label="delete" onClick={onClose} size="large">
                <CloseIcon />
            </IconButton>
        </StyHeader>
    );
};

const StyDrawer = styled(Drawer, {
    name: 'StyDrawer',
})({
    '& .MuiDrawer-paper': {
        padding: '2.5rem',
        width: '700px',
        maxWidth: '700px',
    },
    /* with MUI5, Modal have a default higher z-index than the drawer (1300 vs 1200);
     * inscriptions details panel is therefore hidden without this override */
    zIndex: 1400,
});

type Props = {
    canEdit: boolean;
    exercice: number;
    onClose: FlowAnyFunction;
    open: boolean;
    projectionDetailAmount: number | undefined;
    projectionDetailComment: string | undefined;
    resourceSoftwareId: string;
    updateProjectionParams: UpdateProjectionDetailsParams;
    dimensions: ProjectionVentilationData;
};

const ProjectionSidePanel = (props: Props): React.ReactElement => {
    const {
        canEdit,
        exercice,
        onClose,
        open,
        projectionDetailAmount,
        projectionDetailComment,
        resourceSoftwareId,
        updateProjectionParams,
        dimensions,
    } = props;

    const { t } = useTranslation();

    const { budgetProposal, expenseOrIncome, isDaf } = updateProjectionParams;

    const updateProjectionMutation = useUpdateAtterrissageProjectionDetails({
        budgetProposal,
        expenseOrIncome,
        isDaf,
        dimensions,
    });
    const headerTitle = t('projection.sidePanelTitle', { exercice });
    const amountLabel = t('projection.amount', { exercice });
    const commentLabel = t('projection.justification');

    const updateProjectionDetails = (
        value: string | number,
        field: 'amount' | 'comment',
    ) => {
        const defaultValue = field === 'amount' ? 0 : '';
        const finalValue = getNewFormattedValue(value, defaultValue, field);
        trackEventBudget(
            field === 'amount'
                ? TRACKABLE_EVENTS.budget.budgetOverview.projections
                      .atterrissage.amountUpdated
                : TRACKABLE_EVENTS.budget.budgetOverview.projections
                      .atterrissage.commentUpdated,
        );
        updateProjectionMutation.mutate({
            field,
            value: finalValue,
            resourceSoftwareId,
        });
    };
    const updateAmount = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = event.target.value;
            if (newValue === '') {
                return updateProjectionDetails(0, 'amount');
            }
            return updateProjectionDetails(
                parseNumericInput(newValue),
                'amount',
            );
        },
        [],
    );
    const updateComment = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) =>
            updateProjectionDetails(event.target.value, 'comment'),
        [],
    );
    return (
        <StyDrawer anchor="right" open={open} onClose={onClose}>
            <Header onClose={onClose} title={headerTitle} />
            <Stack spacing={4} sx={{ mt: 12 }}>
                <TextFieldWithDebounce
                    value={projectionDetailAmount}
                    disabled={!canEdit}
                    fullWidth
                    label={amountLabel}
                    variant="outlined"
                    size="small"
                    onChange={updateAmount}
                    InputProps={{
                        disableInjectingGlobalStyles: true,
                        startAdornment,
                        inputComponent: NumberFormatCustom,
                    }}
                />
                <TextFieldWithDebounce
                    value={projectionDetailComment}
                    disabled={!canEdit}
                    fullWidth
                    label={commentLabel}
                    variant="outlined"
                    size="small"
                    onChange={updateComment}
                    rows={4}
                    InputProps={{
                        disableInjectingGlobalStyles: true,
                    }}
                    multiline
                />
            </Stack>
        </StyDrawer>
    );
};

export default ProjectionSidePanel;
